import { create } from 'zustand'
import { apiClient } from '../utils/api'
import getSubscription from '../utils/subscriptions'

export interface Conversation {
  id: string
  player_id: string
  scout_id: string
  other_user_id?: string
  last_message?: string
  last_message_at?: string
  created_at: string
  updated_at: string
}

export interface Message {
  id: string
  conversation_id: string
  sender_id: string
  content: string
  is_read: boolean
  is_reported: boolean
  created_at: string
  updated_at: string
}

interface MessageStore {
  conversations: Conversation[]
  messages: Record<string, Message[]>
  profiles: Record<string, { id: string; name: string; role: string; avatar_url?: string }>
  loading: boolean
  error: string | null
  activeConversationId: string | null
  unreadCount: number
  fetchConversations: (playerId: string) => Promise<void>
  fetchConversationsByRecipient: (recipientId: string) => Promise<void>
  fetchProfilesByIds: (ids: string[]) => Promise<void>
  createConversation: (data: { player_id: string; scout_id: string }) => Promise<Conversation>
  fetchMessages: (conversationId: string) => Promise<void>
  sendMessage: (conversationId: string, content: string, senderId: string) => Promise<void>
  markAsRead: (conversationId: string) => Promise<void>
  reportMessage: (messageId: string) => Promise<void>
  archiveConversation: (conversationId: string) => Promise<void>
  deleteConversation: (conversationId: string) => Promise<void>
  setActiveConversation: (id: string | null) => void
  fetchUnreadCount: (userId: string, role: string, playerId?: string) => Promise<void>
  refreshConversations: (userId: string, role: string, playerId?: string) => Promise<void>
}

export const useMessageStore = create<MessageStore>()(
  (set) => ({
    conversations: [],
    messages: {},
    profiles: {},
    loading: false,
    error: null,
    activeConversationId: null,
    unreadCount: 0,

    fetchConversations: async (playerId: string) => {
      set({ loading: true, error: null })
      try {
        const conversations = await apiClient.getConversations(playerId)
        set({ conversations, loading: false })
      } catch (error) {
        set({ error: 'Failed to load conversations', loading: false })
        console.error('Failed to fetch conversations:', error)
      }
    },

    fetchConversationsByRecipient: async (recipientId: string) => {
      set({ loading: true, error: null })
      try {
        const conversations = await apiClient.getConversationsByRecipient(recipientId)
        set({ conversations, loading: false })
      } catch (error) {
        set({ error: 'Failed to load conversations', loading: false })
        console.error('Failed to fetch conversations:', error)
      }
    },

    fetchProfilesByIds: async (ids: string[]) => {
      try {
        const profiles = await apiClient.getProfilesByIds(ids)
        const nameMap: Record<string, { id: string; name: string; role: string; avatar_url?: string }> = {}
        for (const p of profiles as any[]) {
          const name = p.name || p.full_name || (p.email ? p.email.split('@')[0] : 'Unknown')
          nameMap[p.id] = { id: p.id, name, role: p.role || 'player', avatar_url: p.avatar_url }
        }
        // For any IDs that weren't in profiles, try the players table
        const missingIds = ids.filter(id => !nameMap[id])
        if (missingIds.length > 0) {
          const players = await apiClient.getPlayers()
          for (const pl of players as any[]) {
            if (missingIds.includes(pl.id) && pl.name) {
              nameMap[pl.id] = { id: pl.id, name: pl.name, role: 'player', avatar_url: pl.photo_url }
            }
          }
        }
        set((state) => ({
          profiles: { ...state.profiles, ...nameMap }
        }))
      } catch (error) {
        console.error('Failed to fetch profiles:', error)
      }
    },

    createConversation: async (data) => {
      const sub = await getSubscription()
      const isPro = !!(sub && (sub.status === 'active' || sub.active))
      if (!isPro) {
        const state = useMessageStore.getState()
        if (state.conversations.length >= 1) {
          throw new Error('FREE_LIMIT')
        }
      }
      set({ error: null })
      try {
        const conversation = await apiClient.createConversation(data)
        set((state) => ({ conversations: [conversation, ...state.conversations] }))
        return conversation
      } catch (error) {
        set({ error: 'Failed to create conversation' })
        console.error('Failed to create conversation:', error)
        throw error
      }
    },

    fetchMessages: async (conversationId: string) => {
      set({ loading: true, error: null, activeConversationId: conversationId })
      try {
        const messages = await apiClient.getMessages(conversationId)
        set((state) => ({ messages: { ...state.messages, [conversationId]: messages }, loading: false }))
      } catch (error) {
        set({ error: 'Failed to load messages', loading: false })
        console.error('Failed to fetch messages:', error)
      }
    },

    sendMessage: async (conversationId: string, content: string, senderId: string) => {
      set({ error: null })
      try {
        const message = await apiClient.sendMessage({
          conversation_id: conversationId,
          sender_id: senderId,
          content,
        })
        set((state) => ({
          messages: {
            ...state.messages,
            [conversationId]: [...(state.messages[conversationId] || []), message],
          },
        }))
        // Update last message in conversations
        set((state) => ({
          conversations: state.conversations.map((c) =>
            c.id === conversationId
              ? { ...c, last_message: content, last_message_at: message.created_at }
              : c
          ),
        }))
      } catch (error) {
        set({ error: 'Failed to send message' })
        console.error('Failed to send message:', error)
        throw error
      }
    },

    markAsRead: async (conversationId: string) => {
      try {
        await apiClient.markMessagesAsRead(conversationId)
        set((state) => {
          const convMessages = state.messages[conversationId] || []
          return {
            messages: {
              ...state.messages,
              [conversationId]: convMessages.map((m) => ({ ...m, is_read: true })),
            },
          }
        })
        // Re-fetch accurate count from server instead of local calculation
        const state = useMessageStore.getState()
        const { user } = await import('../stores').then(m => m.useAuthStore.getState())
        if (user) {
          const playerStore = await import('../stores').then(m => m.usePlayerStore.getState())
          const currentPlayer = playerStore.players.find(p =>
            p.scout?.toLowerCase() === user.email?.toLowerCase() ||
            p.email?.toLowerCase() === user.email?.toLowerCase() ||
            p.name?.toLowerCase() === user.name?.toLowerCase()
          )
          if (user.role === 'player' && currentPlayer) {
            await state.fetchUnreadCount(user.id, 'player', currentPlayer.id)
          } else {
            await state.fetchUnreadCount(user.id, user.role)
          }
        }
      } catch (error) {
        console.error('Failed to mark messages as read:', error)
      }
    },

    reportMessage: async (messageId: string) => {
      try {
        await apiClient.reportMessage(messageId)
        set((state) => ({
          messages: Object.fromEntries(
            Object.entries(state.messages).map(([convId, msgs]) => [
              convId,
              msgs.map((m) => (m.id === messageId ? { ...m, is_reported: true } : m)),
            ])
          ),
        }))
      } catch (error) {
        console.error('Failed to report message:', error)
      }
    },

    archiveConversation: async (conversationId: string) => {
      try {
        await apiClient.updateConversation(conversationId, { is_archived: true })
        set((state) => ({
          conversations: state.conversations.filter((c) => c.id !== conversationId),
        }))
      } catch (error) {
        console.error('Failed to archive conversation:', error)
        throw error
      }
    },

    deleteConversation: async (conversationId: string) => {
      try {
        await apiClient.deleteConversation(conversationId)
        set((state) => ({
          conversations: state.conversations.filter((c) => c.id !== conversationId),
          messages: Object.fromEntries(
            Object.entries(state.messages).filter(([key]) => key !== conversationId)
          ),
        }))
        set({ activeConversationId: null })
      } catch (error) {
        console.error('Failed to delete conversation:', error)
        throw error
      }
    },

    setActiveConversation: (id: string | null) => set({ activeConversationId: id }),

    fetchUnreadCount: async (userId: string, role: string, playerId?: string) => {
      try {
        let count = 0
        if (role === 'player' && playerId) {
          count = await apiClient.getUnreadCountForPlayer(playerId, userId)
        } else {
          count = await apiClient.getUnreadCountByRecipient(userId)
        }
        set({ unreadCount: count })
      } catch (error) {
        console.error('Failed to fetch unread count:', error)
      }
    },

    refreshConversations: async (userId: string, role: string, playerId?: string) => {
      try {
        if (role === 'player' && playerId) {
          const conversations = await apiClient.getConversations(playerId)
          set({ conversations })
        } else {
          const conversations = await apiClient.getConversationsByRecipient(userId)
          set({ conversations })
        }
        // Also refresh unread count
        const state = useMessageStore.getState()
        await state.fetchUnreadCount(userId, role, playerId)
      } catch (error) {
        console.error('Failed to refresh conversations:', error)
      }
    },
  })
)